<template>
  <a
    :href="item.path"
    class="nl"
    :class="{
      'is-active': active,
      'is-hot': item.hot,
      'is-sub': sub,
      'is-collapsed': collapsed,
      'is-focus': focused,
    }"
    :style="{ '--i': index }"
    :aria-current="active ? 'page' : undefined"
    :aria-label="collapsed ? item.name + '. ' + item.hint : undefined"
    @click.prevent="$emit('go', item)"
    @mouseenter="$emit('peek', $event, item)"
    @mouseleave="$emit('unpeek')"
    @focus="$emit('peek', $event, item)"
    @blur="$emit('unpeek')"
  >
    <span class="nl__bar" aria-hidden="true"></span>
    <span class="nl__icon" aria-hidden="true">
      <font-awesome-icon :icon="['fa-solid', item.icon]" />
    </span>
    <span v-if="!collapsed" class="nl__text">
      <span class="nl__name">
        {{ item.name }}
        <span v-if="item.hot && sub" class="nl__star" title="Часто открываете">●</span>
      </span>
      <span v-if="showHint" class="nl__hint">{{ item.hint }}</span>
    </span>
    <span
      v-if="badge"
      class="nl__badge"
      :class="['nl__badge--' + badgeTone, { 'nl__badge--dot': collapsed }]"
    >{{ badge }}</span>
  </a>
</template>

<script>
// Пункт левого меню: иконка, название, пояснение «зачем сюда», живой счётчик.
// Жирным — то, чем человек и его роль пользуются чаще всего (menuLogic.mjs).
export default {
  name: 'NavLink',
  props: {
    item: { type: Object, required: true },
    active: { type: Boolean, default: false },
    collapsed: { type: Boolean, default: false },
    sub: { type: Boolean, default: false },
    badge: { type: String, default: '' },
    badgeTone: { type: String, default: 'red' },
    showHint: { type: Boolean, default: true },
    focused: { type: Boolean, default: false },
    index: { type: Number, default: 0 },
  },
  emits: ['go', 'peek', 'unpeek'],
}
</script>

<style scoped>
.nl {
  position: relative; display: flex; align-items: center; gap: 12px;
  margin: 2px 10px; padding: 9px 10px; min-height: 48px;
  border-radius: 12px; color: var(--sn-text); text-decoration: none; outline: none;
  transition: background .2s ease, color .2s ease, transform .25s cubic-bezier(.2, .8, .2, 1);
  animation: nl-in .5s cubic-bezier(.2, .8, .2, 1) backwards;
  animation-delay: calc(80ms + var(--i) * 28ms);
}
.nl.is-sub { min-height: 40px; padding: 7px 10px; margin-left: 18px; }
.nl:hover, .nl.is-focus { background: var(--sn-hover); color: #fff; transform: translateX(3px); }
.nl:focus-visible { box-shadow: 0 0 0 2px var(--sn-gold); }

.nl__bar {
  position: absolute; left: -10px; top: 10px; bottom: 10px; width: 3px; border-radius: 0 3px 3px 0;
  background: linear-gradient(180deg, #f0d27c, #b8923a); transform: scaleY(0); transition: transform .3s cubic-bezier(.2, .9, .3, 1.2);
}
.nl.is-active { background: var(--sn-active); color: #fff; }
.nl.is-active .nl__bar { transform: scaleY(1); }

.nl__icon {
  flex: 0 0 auto; display: grid; place-items: center; width: 32px; height: 32px; border-radius: 10px;
  font-size: 14px; color: var(--sn-muted); background: rgba(255, 255, 255, .04);
  transition: color .2s, background .2s, box-shadow .3s, transform .3s;
}
.is-sub .nl__icon { width: 26px; height: 26px; font-size: 12px; border-radius: 8px; }
.nl:hover .nl__icon { color: #fff; transform: scale(1.06); }
.nl.is-active .nl__icon {
  color: #1a1d25; background: linear-gradient(135deg, #f0d27c, #c9a24a);
  box-shadow: 0 6px 18px -6px rgba(226, 196, 110, .7);
}
.nl.is-hot:not(.is-active) .nl__icon { color: var(--sn-gold); }

.nl__text { display: flex; flex-direction: column; min-width: 0; flex: 1; gap: 2px; }
.nl__name {
  font-size: 14px; font-weight: 500; line-height: 1.2; letter-spacing: .005em;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.is-sub .nl__name { font-size: 13.5px; }
.nl.is-hot .nl__name { font-weight: 700; color: #fff; }
.nl__star { margin-left: 4px; font-size: 7px; vertical-align: middle; color: var(--sn-gold); }
.nl__hint {
  font-size: 11.5px; line-height: 1.25; color: var(--sn-muted);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.nl.is-active .nl__hint { color: #c8b98c; }

.nl__badge {
  flex: 0 0 auto; min-width: 22px; height: 22px; padding: 0 7px; border-radius: 999px;
  font-size: 11px; font-weight: 800; line-height: 22px; text-align: center; color: #fff;
}
.nl__badge--red { background: #e5484d; box-shadow: 0 0 0 0 rgba(229, 72, 77, .6); animation: nl-pulse 2.4s ease-out infinite; }
.nl__badge--amber { background: #d99a1e; color: #1a1d25; }
.nl__badge--gray { background: rgba(255, 255, 255, .14); }
.nl__badge--dot {
  position: absolute; top: 4px; right: 4px; min-width: 16px; height: 16px; padding: 0 4px;
  font-size: 9px; line-height: 16px;
}

.nl.is-collapsed { justify-content: center; margin: 3px 10px; padding: 8px 0; min-height: 46px; }
.nl.is-collapsed.is-sub { margin-left: 10px; }
.nl.is-collapsed:hover { transform: none; }
.nl.is-collapsed .nl__bar { left: -10px; }

@keyframes nl-in { from { opacity: 0; transform: translateX(-12px); } }
@keyframes nl-pulse {
  0% { box-shadow: 0 0 0 0 rgba(229, 72, 77, .55); }
  70%, 100% { box-shadow: 0 0 0 8px rgba(229, 72, 77, 0); }
}
@media (prefers-reduced-motion: reduce) {
  .nl, .nl__badge { animation: none !important; }
}
</style>
